import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ToothChart from './ToothChart';
import {
  X, Save, FileText, Image as ImageIcon, Trash2, Paperclip,
  Stethoscope, Search, Loader, History, Pill, AlertTriangle,
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { StatusBadge, PaymentBadge } from './Badges';
import StatusSelect from './StatusSelect';
import ConfirmDialog from './ConfirmDialog';

// ── helpers ───────────────────────────────────────────────────────────────────
function fmt(n) { return (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }); }

// finalPrice = catalogPrice - (catalogPrice × discountPercent / 100)
function calcFinal(catalogPrice, discountPercent) {
  const p = Number(catalogPrice) || 0;
  const d = Math.min(100, Math.max(0, Number(discountPercent) || 0));
  return Math.max(0, p - p * d / 100);
}

// Normalise: old string records → objects; ensures backward compatibility
function normaliseDiagnosis(raw) {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map(d => {
    if (typeof d === 'string') {
      return { treatmentId: null, name: d, catalogPrice: 0, quantity: 1, discountPercent: 0, finalPrice: 0, fdiRequired: false, teethForTreatment: [] };
    }
    return {
      treatmentId:       d.treatmentId       ?? null,
      name:              d.name              ?? '',
      catalogPrice:      Number(d.catalogPrice)      || 0,
      quantity:          Number(d.quantity)          || 1,
      discountPercent:   Number(d.discountPercent)   || 0,
      finalPrice:        Number(d.finalPrice)        || 0,
      fdiRequired:       d.fdiRequired        ?? false,
      teethForTreatment: Array.isArray(d.teethForTreatment) ? d.teethForTreatment : [],
    };
  });
}

// ── Treatment line-item table ─────────────────────────────────────────────────
// The dropdown is rendered via a Portal into document.body so it escapes
// the modal's "overflow-y: auto" clipping that was hiding it.
function TreatmentLineItems({ items, onChange, editable }) {
  const [q, setQ]                 = useState('');
  const [results, setResults]     = useState([]);
  const [open, setOpen]           = useState(false);
  const [searching, setSearching] = useState(false);
  const [dropPos, setDropPos]     = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef(null);
  const dbRef    = useRef(null);

  // ── search ──────────────────────────────────────────────────────────────────
  const doSearch = useCallback(async (text) => {
    setSearching(true);
    try {
      const r = await api.get('/treatments', { params: { search: text } });
      setResults(r.data.treatments || []);
      setOpen(true);
    } catch (err) {
      console.error('Treatment search error', err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!q) return;
    clearTimeout(dbRef.current);
    dbRef.current = setTimeout(() => doSearch(q), 260);
    return () => clearTimeout(dbRef.current);
  }, [q, doSearch]);

  // Position the portal dropdown under the input
  function openDropdown() {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropPos({
        top:   rect.bottom + window.scrollY + 4,
        left:  rect.left   + window.scrollX,
        width: rect.width,
      });
    }
    doSearch(q);
  }

  // Close when clicking outside
  useEffect(() => {
    function handleOutside(e) {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        // Don't close if click is inside the portal dropdown
        const portal = document.getElementById('treatment-dropdown-portal');
        if (portal && portal.contains(e.target)) return;
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // ── id normaliser ────────────────────────────────────────────────────────────
  function tid(val) {
    if (!val) return '';
    if (typeof val === 'object') return String(val._id || val);
    return String(val);
  }

  // ── pick ─────────────────────────────────────────────────────────────────────
  function pick(t) {
    onChange([...items, {
      treatmentId:        t._id,
      name:               t.name,
      catalogPrice:       Number(t.price) || 0,
      quantity:           1,
      discountPercent:    0,
      finalPrice:         Number(t.price) || 0,
      fdiRequired:        false,
      teethForTreatment:  [],
    }]);
    setQ('');
    setOpen(false);
  }

  function remove(idx) { onChange(items.filter((_, i) => i !== idx)); }

  function setQty(idx, val) {
    const qty = Math.max(1, Number(val) || 1);
    onChange(items.map((item, i) => {
      if (i !== idx) return item;
      const unitFinal = calcFinal(item.catalogPrice, item.discountPercent);
      return { ...item, quantity: qty, finalPrice: parseFloat((unitFinal * qty).toFixed(2)) };
    }));
  }

  function setDiscount(idx, discVal) {
    const disc = Math.min(100, Math.max(0, Number(discVal) || 0));
    onChange(items.map((item, i) => {
      if (i !== idx) return item;
      const qty = item.quantity || 1;
      return { ...item, discountPercent: disc, finalPrice: parseFloat((calcFinal(item.catalogPrice, disc) * qty).toFixed(2)) };
    }));
  }

  function setFinalPrice(idx, val) {
    const final = Math.max(0, Number(val) || 0);
    onChange(items.map((item, i) => {
      if (i !== idx) return item;
      const qty = item.quantity || 1;
      const unitFinal = qty > 0 ? final / qty : final;
      const impliedDisc = item.catalogPrice > 0
        ? Math.round(((item.catalogPrice - unitFinal) / item.catalogPrice) * 10000) / 100
        : 0;
      return { ...item, finalPrice: final, discountPercent: Math.max(0, impliedDisc) };
    }));
  }

  // Toggle FDI chart requirement for a treatment
  function setFDIRequired(idx, val) {
    onChange(items.map((item, i) =>
      i !== idx ? item : { ...item, fdiRequired: val, teethForTreatment: val ? item.teethForTreatment : [] }
    ));
  }

  // When teeth are selected for a treatment, also update quantity to match count
  function setTeethForTreatment(idx, teeth) {
    onChange(items.map((item, i) => {
      if (i !== idx) return item;
      const qty = teeth.length > 0 ? teeth.length : 1;
      const unitFinal = calcFinal(item.catalogPrice, item.discountPercent);
      return { ...item, teethForTreatment: teeth, quantity: qty, finalPrice: parseFloat((unitFinal * qty).toFixed(2)) };
    }));
  }

  const addedIds   = new Set(items.map(i => tid(i.treatmentId)));
  const available  = results.filter(r => !addedIds.has(tid(r._id)));
  const totalFinal = items.reduce((s, i) => s + (Number(i.finalPrice) || 0), 0);

  // ── Portal dropdown ──────────────────────────────────────────────────────────
  const dropdownPortal = open ? ReactDOM.createPortal(
    <div
      id="treatment-dropdown-portal"
      className="card"
      style={{
        position: 'absolute',
        top:      dropPos.top,
        left:     dropPos.left,
        width:    dropPos.width,
        zIndex:   9999,
        padding:  0,
        maxHeight: 260,
        overflowY: 'auto',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      {available.length === 0 ? (
        <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--color-ink-faint)' }}>
          {results.length === 0
            ? 'No treatments in catalog yet. Add them via Treatment Catalog in the menu.'
            : 'All catalog treatments have already been added to this visit.'}
        </div>
      ) : (
        available.map(t => (
          <button
            key={t._id}
            type="button"
            onMouseDown={e => { e.preventDefault(); pick(t); }}
            style={{ width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 10 }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-primary-soft)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Stethoscope size={14} color="var(--color-primary)" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{t.name}</div>
              {t.description && (
                <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.description}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              {(t.price || 0) > 0
                ? <span style={{ fontWeight: 700, color: 'var(--color-success)', fontSize: 13 }}>₹{fmt(t.price)}</span>
                : <span style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>No price set</span>}
              {t.category && (
                <div style={{ fontSize: 10, color: 'var(--color-ink-faint)', marginTop: 1, textTransform: 'capitalize' }}>
                  {t.category}
                </div>
              )}
            </div>
          </button>
        ))
      )}
    </div>,
    document.body
  ) : null;

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Line-item table */}
      {items.length > 0 && (
        <div style={{ marginBottom: 14, border: 'var(--color-border) 1px solid', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 65px 60px 95px 36px', padding: '7px 12px', fontSize: 11, fontWeight: 700, color: 'var(--color-ink-faint)', background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)' }}>
            <span>Treatment</span>
            <span style={{ textAlign: 'right' }}>Catalog ₹</span>
            <span style={{ textAlign: 'right' }}>Disc %</span>
            <span style={{ textAlign: 'right' }}>Qty</span>
            <span style={{ textAlign: 'right' }}>Final ₹</span>
            <span />
          </div>

          {items.map((item, idx) => (
            <div key={idx} style={{ borderBottom: '1px solid var(--color-border)', background: idx % 2 === 0 ? 'transparent' : 'var(--color-surface)' }}>
              {/* Main row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 65px 60px 95px 36px', padding: '8px 12px', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, paddingRight: 8 }}>
                  <Stethoscope size={12} color="var(--color-primary)" style={{ flexShrink: 0 }} />
                  <span>{item.name}</span>
                  {/* FDI checkbox — inline next to treatment name */}
                  {editable && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: item.fdiRequired ? 'var(--color-primary)' : 'var(--color-ink-faint)', cursor: 'pointer', marginLeft: 4, padding: '2px 6px', borderRadius: 4, background: item.fdiRequired ? 'var(--color-primary-soft)' : 'transparent', border: '1px solid ' + (item.fdiRequired ? 'var(--color-primary)' : 'var(--color-border)'), whiteSpace: 'nowrap' }}
                      title="Tick to attach an FDI tooth chart to this treatment">
                      <input type="checkbox" checked={!!item.fdiRequired}
                        onChange={e => setFDIRequired(idx, e.target.checked)}
                        style={{ width: 11, height: 11, accentColor: 'var(--color-primary)', margin: 0 }} />
                      🦷 FDI
                    </label>
                  )}
                  {!editable && item.fdiRequired && item.teethForTreatment.length > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--color-primary)' }}>🦷 {item.teethForTreatment.map(t => `#${t}`).join(', ')}</span>
                  )}
                </div>
                <div style={{ textAlign: 'right', fontSize: 13, color: 'var(--color-ink-soft)' }}>
                  {(item.catalogPrice || 0) > 0 ? `₹${fmt(item.catalogPrice)}` : '—'}
                </div>
                {editable ? (
                  <input type="number" min="0" max="100" step="any" className="input"
                    style={{ padding: '4px 6px', fontSize: 12, textAlign: 'right', margin: '0 4px' }}
                    value={item.discountPercent === 0 ? '' : item.discountPercent}
                    placeholder="0%"
                    onChange={e => setDiscount(idx, e.target.value)} />
                ) : (
                  <div style={{ textAlign: 'right', fontSize: 13 }}>
                    {(item.discountPercent || 0) > 0
                      ? <span style={{ color: 'var(--color-warning)', fontWeight: 600 }}>{item.discountPercent}%</span>
                      : <span style={{ color: 'var(--color-ink-faint)' }}>—</span>}
                  </div>
                )}
                {/* Qty — auto-filled from FDI teeth count when fdiRequired */}
                {editable ? (
                  <input type="number" min="1" step="1" className="input"
                    style={{ padding: '4px 6px', fontSize: 12, textAlign: 'right', margin: '0 4px', background: item.fdiRequired ? 'var(--color-primary-soft)' : undefined }}
                    value={item.quantity || 1}
                    readOnly={item.fdiRequired && item.teethForTreatment.length > 0}
                    title={item.fdiRequired ? 'Quantity set automatically from selected teeth' : 'Enter quantity'}
                    onChange={e => !item.fdiRequired && setQty(idx, e.target.value)} />
                ) : (
                  <div style={{ textAlign: 'right', fontSize: 13, color: 'var(--color-ink-soft)' }}>
                    {item.quantity || 1}
                  </div>
                )}
                {editable ? (
                  <input type="number" min="0" step="any" className="input"
                    style={{ padding: '4px 6px', fontSize: 12, textAlign: 'right', fontWeight: 700, color: 'var(--color-success)', borderColor: 'var(--color-success)', margin: '0 4px' }}
                    value={item.finalPrice === 0 ? '' : item.finalPrice}
                    placeholder="0"
                    onChange={e => setFinalPrice(idx, e.target.value)} />
                ) : (
                  <div style={{ textAlign: 'right', fontWeight: 700, color: 'var(--color-success)', fontSize: 13 }}>
                    ₹{fmt(item.finalPrice)}
                  </div>
                )}
                {editable && (
                  <button type="button" className="btn btn-icon btn-ghost" onClick={() => remove(idx)} title="Remove">
                    <Trash2 size={13} color="var(--color-danger)" />
                  </button>
                )}
              </div>

              {/* Per-treatment FDI tooth chart — shown when fdiRequired is ticked */}
              {item.fdiRequired && (
                <div style={{ padding: '0 12px 12px', borderTop: '1px dashed var(--color-border)' }}>
                  <ToothChart
                    selected={item.teethForTreatment}
                    onChange={editable ? (teeth) => setTeethForTreatment(idx, teeth) : () => {}}
                    editable={editable}
                    compact
                  />
                  {item.teethForTreatment.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', marginTop: 4 }}>
                      {item.teethForTreatment.length} tooth/teeth selected → Qty auto-set to {item.teethForTreatment.length}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 65px 60px 95px 36px', padding: '8px 12px', background: 'var(--color-success-soft)', borderTop: '2px solid var(--color-success)' }}>
            <span style={{ fontWeight: 700, fontSize: 13, gridColumn: '1 / 5', color: 'var(--color-success)' }}>Total Charged</span>
            <span style={{ textAlign: 'right', fontWeight: 800, fontSize: 14, color: 'var(--color-success)' }}>₹{fmt(totalFinal)}</span>
            <span />
          </div>

          {editable && (
            <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', padding: '6px 12px', lineHeight: 1.6, borderTop: '1px solid var(--color-border)' }}>
              <strong>Catalog ₹</strong> is read-only. <strong>Disc %</strong> auto-fills Final ₹. <strong>Final ₹</strong> can be typed directly — Disc % updates to match. Total is added to billing on save.
            </div>
          )}
        </div>
      )}

      {/* Search input — portal dropdown escapes modal overflow clipping */}
      {editable && (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)', pointerEvents: 'none' }} />
            <input
              ref={inputRef}
              className="input"
              style={{ paddingLeft: 28, fontSize: 13 }}
              value={q}
              onChange={e => { setQ(e.target.value); if (!e.target.value) doSearch(''); }}
              onFocus={openDropdown}
              placeholder={items.length === 0 ? 'Click to select treatments from catalog…' : 'Add another treatment from catalog…'}
              autoComplete="off"
            />
            {searching && (
              <Loader size={12} style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)', animation: 'spin 1s linear infinite' }} />
            )}
          </div>
          {dropdownPortal}
        </div>
      )}

      {items.length === 0 && !editable && (
        <div style={{ padding: '12px 10px', fontSize: 13, color: 'var(--color-ink-faint)', fontStyle: 'italic' }}>
          No treatment recorded for this visit.
        </div>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function PatientDetailModal({ patient, editable, onClose, onUpdated }) {
  const { showToast } = useToast();
  const fileInputRef  = useRef(null);

  const [form, setForm] = useState({
    name:           patient.name || '',
    age:            patient.age  || '',
    gender:         patient.gender || 'Other',
    phone:          patient.phone  || '',
    address:        patient.address || '',
    reasonForVisit: patient.reasonForVisit || '',
    medicalHistory: patient.medicalHistory || '',
    doctorName:     patient.doctorName     || '',
    doctorNotes:    patient.doctorNotes    || '',
    diagnosis:      normaliseDiagnosis(patient.diagnosis),
    treatmentNotes: patient.treatmentNotes || '',
    teethSelected:  Array.isArray(patient.teethSelected) ? patient.teethSelected : [],
    // Referral tracking
    referredBy:     patient.referredBy    || '',
    referredTo:     patient.referredTo    || '',
    referralNotes:  patient.referralNotes || '',
    // Treatment plan
    treatmentPlan:  Array.isArray(patient.treatmentPlan) ? patient.treatmentPlan : [],
  });

  const [pharmacyHistory, setPharmacyHistory] = useState([]);

  // Doctor Name is selected from the clinic's actual active doctor
  // profiles — same pattern used in Create Token and Billing → Doctor Cost —
  // so the assigned doctor can be corrected after the token was created.
  const [doctorOptions, setDoctorOptions] = useState([]);
  useEffect(() => {
    api.get('/auth/staff')
      .then(r => setDoctorOptions((r.data.staff || []).filter(s => s.role === 'doctor' && s.isActive)))
      .catch(() => setDoctorOptions([]));
  }, []);
  const [loadingPharmacy, setLoadingPharmacy] = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [uploading, setUploading]             = useState(false);
  const [deleteAttachmentId, setDeleteAttachmentId] = useState(null);
  const [deletingAttachment, setDeletingAttachment] = useState(false);

  useEffect(() => {
    async function loadPharmacy() {
      setLoadingPharmacy(true);
      try {
        const res = await api.get('/pharmacy/transactions', {
          params: { type: 'outgoing', search: patient.patientUID, limit: 50 },
        });
        setPharmacyHistory(res.data.transactions || []);
      } catch { setPharmacyHistory([]); }
      finally { setLoadingPharmacy(false); }
    }
    loadPharmacy();
  }, [patient._id, patient.patientUID]);

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function handleSaveDetails() {
    setSaving(true);
    try {
      const res = await api.put(`/patients/${patient._id}`, {
        ...form, age: form.age ? Number(form.age) : undefined,
      });
      showToast('Patient details saved successfully.', 'success');
      onUpdated && onUpdated(res.data.patient);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save changes.', 'error');
    } finally { setSaving(false); }
  }

  async function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      showToast('Only image files or PDF documents are allowed.', 'error'); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('File is too large. Maximum size is 10 MB.', 'error'); return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/patients/${patient._id}/attachments`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('File uploaded.', 'success');
      onUpdated && onUpdated(res.data.patient);
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed.', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function confirmDeleteAttachment() {
    if (!deleteAttachmentId) return;
    setDeletingAttachment(true);
    try {
      const res = await api.delete(`/patients/${patient._id}/attachments/${deleteAttachmentId}`);
      showToast('Attachment deleted.', 'success');
      onUpdated && onUpdated(res.data.patient);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete attachment.', 'error');
    } finally {
      setDeletingAttachment(false);
      setDeleteAttachmentId(null);
    }
  }

  const apiOrigin  = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
  const treatTotal = form.diagnosis.reduce((s, d) => s + (Number(d.finalPrice) || 0), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '22px 24px 0' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>{patient.name}</h2>
            <div style={{ fontSize: 13, color: 'var(--color-ink-faint)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
              {patient.patientUID} · Token #{patient.tokenNumber}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <StatusBadge status={patient.status} />
              <PaymentBadge status={patient.billing?.paymentStatus || 'unpaid'} />
            </div>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Medical History — always shown here, right under the header, so
            it's the first thing visible whenever this patient is opened
            during a visit. Not tucked away in the scrollable form below,
            and not conditional on edit mode — this shows for every role. */}
        {((form.medicalHistory && form.medicalHistory.trim()) || (patient.priorMedicalHistory && patient.priorMedicalHistory.length > 0)) && (
          <div style={{
            margin: '14px 24px 0', padding: '12px 14px', borderRadius: 8,
            background: 'var(--color-danger-soft)', border: '1px solid var(--color-danger)',
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <AlertTriangle size={18} color="var(--color-danger)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--color-danger)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 2 }}>
                Medical History
              </div>
              {form.medicalHistory && form.medicalHistory.trim() && (
                <div style={{ fontSize: 13, color: 'var(--color-ink)', whiteSpace: 'pre-wrap' }}>
                  {form.medicalHistory}
                </div>
              )}
              {/* Entries recorded on this patient's OTHER visits — the field
                  gets overwritten (not appended to) at each new booking, so
                  something noted earlier could otherwise go unseen here. */}
              {patient.priorMedicalHistory && patient.priorMedicalHistory.length > 0 && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--color-danger)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-warning)', textTransform: 'uppercase', marginBottom: 2 }}>
                    Also on file from earlier visits
                  </div>
                  {patient.priorMedicalHistory.map((h, i) => (
                    <div key={i} style={{ fontSize: 12, color: 'var(--color-ink-soft)', whiteSpace: 'pre-wrap' }}>{h}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: 'calc(90vh - 100px)' }}>
          {/* Status */}
          <div className="field">
            <label className="label">Change Status</label>
            <StatusSelect patient={patient} onUpdated={onUpdated} />
          </div>

          {/* Patient Details */}
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: '20px 0 12px', color: 'var(--color-ink-soft)' }}>
            Patient Details {editable && <span style={{ fontWeight: 400 }}>(editable)</span>}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="field"><label className="label">Full Name</label>
              <input className="input" value={form.name} disabled={!editable} onChange={e => update('name', e.target.value)} /></div>
            <div className="field"><label className="label">Age</label>
              <input className="input" type="number" value={form.age} disabled={!editable} onChange={e => update('age', e.target.value)} /></div>
            <div className="field"><label className="label">Gender</label>
              <select className="select" value={form.gender} disabled={!editable} onChange={e => update('gender', e.target.value)}>
                <option>Male</option><option>Female</option><option>Other</option></select></div>
            <div className="field"><label className="label">Phone</label>
              <input className="input" value={form.phone} disabled={!editable} onChange={e => update('phone', e.target.value)} /></div>
            <div className="field"><label className="label">Doctor</label>
              {editable ? (
                <select className="select" value={form.doctorName} onChange={e => update('doctorName', e.target.value)}>
                  <option value="">Select doctor…</option>
                  {doctorOptions.map(d => (
                    <option key={d._id} value={d.displayName || d.username}>{d.displayName || d.username}</option>
                  ))}
                  {/* Keep whatever's already assigned selectable even if that doctor is no longer active */}
                  {form.doctorName && !doctorOptions.some(d => (d.displayName || d.username) === form.doctorName) && (
                    <option value={form.doctorName}>{form.doctorName}</option>
                  )}
                </select>
              ) : (
                <input className="input" value={form.doctorName || '—'} disabled />
              )}
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label className="label">Address</label>
              <input className="input" value={form.address} disabled={!editable} onChange={e => update('address', e.target.value)} /></div>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label className="label">Reason for Visit</label>
              <input className="input" value={form.reasonForVisit} disabled={!editable} onChange={e => update('reasonForVisit', e.target.value)} /></div>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label className="label">Medical History</label>
              <textarea className="textarea" value={form.medicalHistory} disabled={!editable} onChange={e => update('medicalHistory', e.target.value)} /></div>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label className="label">Doctor's Notes</label>
              <textarea className="textarea" value={form.doctorNotes} disabled={!editable}
                onChange={e => update('doctorNotes', e.target.value)}
                placeholder={editable ? 'Clinical observations, follow-up instructions...' : 'No notes yet.'} /></div>
          </div>

          {/* Referral Tracking */}
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: '20px 0 10px', color: 'var(--color-ink-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
            🔄 Referral Tracking
          </h3>
          <div className="referral-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div className="field">
              <label className="label">Referred By</label>
              <input className="input" value={form.referredBy} disabled={!editable}
                onChange={e => update('referredBy', e.target.value)}
                placeholder="Who referred this patient?" />
            </div>
            <div className="field">
              <label className="label">Referred To</label>
              <input className="input" value={form.referredTo} disabled={!editable}
                onChange={e => update('referredTo', e.target.value)}
                placeholder="Referred out to which specialist?" />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="label">Referral Notes</label>
              <input className="input" value={form.referralNotes} disabled={!editable}
                onChange={e => update('referralNotes', e.target.value)}
                placeholder="Reason for referral, specialist contact…" />
            </div>
          </div>

          {/* Treatment Plan */}
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: '20px 0 10px', color: 'var(--color-ink-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
            📋 Treatment Plan (Future Work)
          </h3>
          <div style={{ marginBottom: 14 }}>
            {form.treatmentPlan.map((plan, pi) => (
              <div key={pi} className="treatment-plan-row" style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 120px', gap: 8, marginBottom: 8, padding: '10px 12px', background: 'var(--color-surface)', borderRadius: 8, border: '1px solid var(--color-border)', alignItems: 'center' }}>
                <input className="input" value={plan.description} disabled={!editable}
                  onChange={e => { const p = [...form.treatmentPlan]; p[pi] = {...p[pi], description: e.target.value}; update('treatmentPlan', p); }}
                  placeholder="e.g. RCT on tooth 36" style={{ fontSize: 13 }} />
                <select className="select" value={plan.priority} disabled={!editable}
                  onChange={e => { const p = [...form.treatmentPlan]; p[pi] = {...p[pi], priority: e.target.value}; update('treatmentPlan', p); }}
                  style={{ fontSize: 12 }}>
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🟢 Low</option>
                </select>
                <select className="select" value={plan.status} disabled={!editable}
                  onChange={e => { const p = [...form.treatmentPlan]; p[pi] = {...p[pi], status: e.target.value}; update('treatmentPlan', p); }}
                  style={{ fontSize: 12 }}>
                  <option value="planned">📋 Planned</option>
                  <option value="in-progress">⚙️ In Progress</option>
                  <option value="completed">✅ Completed</option>
                  <option value="cancelled">❌ Cancelled</option>
                </select>
                {editable && (
                  <button type="button" className="btn btn-danger btn-sm"
                    onClick={() => { const p = [...form.treatmentPlan]; p.splice(pi,1); update('treatmentPlan', p); }}
                    style={{ fontSize: 12 }}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            {editable && (
              <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 4 }}
                onClick={() => update('treatmentPlan', [...form.treatmentPlan, { description: '', priority: 'medium', status: 'planned', estimatedCost: 0, notes: '' }])}>
                + Add Treatment Plan Item
              </button>
            )}
            {form.treatmentPlan.length === 0 && !editable && (
              <div style={{ fontSize: 13, color: 'var(--color-ink-faint)', fontStyle: 'italic' }}>No treatment plan recorded.</div>
            )}
          </div>

          {/* Treatment Done */}
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: '24px 0 4px', color: 'var(--color-ink-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Stethoscope size={15} color="var(--color-primary)" /> Treatment Done (Diagnosis)
          </h3>
          {editable && treatTotal > 0 && (
            <div style={{ fontSize: 12, padding: '6px 10px', marginBottom: 10, background: 'var(--color-success-soft)', borderRadius: 6, color: 'var(--color-success)', fontWeight: 600 }}>
              ₹{fmt(treatTotal)} total will be added to this patient's billing when you save.
            </div>
          )}
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
            <TreatmentLineItems
              items={form.diagnosis}
              onChange={val => update('diagnosis', val)}
              editable={editable}
            />
          </div>
          {/* Tooth charts are now per-treatment (tick the 🦷 FDI checkbox on each treatment row) */}

          <div className="field" style={{ marginBottom: 14 }}>
            <label className="label">Treatment Notes</label>
            <textarea className="textarea" value={form.treatmentNotes} disabled={!editable}
              onChange={e => update('treatmentNotes', e.target.value)}
              placeholder="Teeth details, observations, follow-up…" />
          </div>

          {/* Pharmacy History */}
          {(pharmacyHistory.length > 0 || loadingPharmacy) && (
            <>
              <h3 style={{ fontSize: 14, fontWeight: 700, margin: '20px 0 12px', color: 'var(--color-ink-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <History size={15} color="var(--color-primary)" /> Pharmacy History
                <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-ink-faint)' }}>
                  ({pharmacyHistory.length} transaction{pharmacyHistory.length !== 1 ? 's' : ''})
                </span>
              </h3>
              {loadingPharmacy
                ? <div className="skeleton" style={{ height: 56, borderRadius: 8 }} />
                : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {pharmacyHistory.map(t => (
                      <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 }}>
                        <Pill size={13} color="var(--color-success)" style={{ flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, flex: 1 }}>{t.itemName}</span>
                        <span style={{ color: 'var(--color-ink-faint)' }}>×{t.quantity} {t.unit}</span>
                        <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>₹{fmt((t.sellingPrice || 0) * t.quantity)}</span>
                        <span style={{ color: 'var(--color-ink-faint)', fontSize: 12 }}>{t.date}</span>
                      </div>
                    ))}
                  </div>
                )}
            </>
          )}

          {editable && (
            <button className="btn btn-primary" onClick={handleSaveDetails} disabled={saving} style={{ marginTop: 20 }}>
              <Save size={16} />{saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}

          {/* Attached Documents — its own separated section, not blended
              into the rest of the scrolling form */}
          <div style={{
            marginTop: 24, padding: 16, border: '1px solid var(--color-border)',
            borderRadius: 10, background: 'var(--color-surface)',
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 12px', color: 'var(--color-ink-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Paperclip size={15} color="var(--color-primary)" />
              Attached Documents
              <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-ink-faint)' }}>
                ({patient.attachments?.length || 0})
              </span>
            </h3>
            {patient.attachments && patient.attachments.length > 0 ? (
              <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
                {patient.attachments.map(a => (
                  <div key={a._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-bg)' }}>
                    {a.fileType === 'pdf'
                      ? <FileText size={18} color="var(--color-danger)" />
                      : <ImageIcon size={18} color="var(--color-info)" />}
                    <a href={`${apiOrigin}${a.filePath}`} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 13, flex: 1, color: 'var(--color-ink)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.originalName}
                    </a>
                    {editable && (
                      <button className="btn btn-icon btn-ghost" onClick={() => setDeleteAttachmentId(a._id)}>
                        <Trash2 size={16} color="var(--color-danger)" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--color-ink-faint)', marginBottom: 14 }}>No files attached yet.</p>
            )}
            {editable && (
              <>
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleFileSelect} />
                <button className="btn btn-outline btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <Paperclip size={14} />{uploading ? 'Uploading...' : 'Attach image or PDF'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteAttachmentId}
        title="Delete this attachment?"
        message="This file will be permanently removed. This cannot be undone."
        confirmLabel={deletingAttachment ? 'Deleting...' : 'Delete file'}
        danger onConfirm={confirmDeleteAttachment} onCancel={() => setDeleteAttachmentId(null)}
      />
      <style>{`@keyframes spin { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(360deg); } }`}</style>
    </div>
  );
}
